import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList, articles } from '@/mocks/anime';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-3">
              番剧未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              该番剧可能已下架或链接有误
            </p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 text-sm font-medium rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              回到首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryMap: Record<string, string> = {
    rexue: '热血', lianai: '恋爱', qihuan: '奇幻', gaoxiao: '搞笑',
    xuanyi: '悬疑', richang: '日常', zhandou: '战斗', kehuan: '科幻',
  };

  const relatedAnime = animeList
    .filter((a) => a.id !== anime.id && (a.category === anime.category || a.tags.some((t) => anime.tags.includes(t))))
    .slice(0, 4);

  const relatedArticles = articles.filter((a) => a.relatedAnimeIds.includes(anime.id));

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${anime.title} - 91动漫在线观看`,
            description: anime.description.substring(0, 160),
            url: `https://www.91anime.com/anime/${anime.id}`,
            datePublished: `${anime.year}-01-01`,
            dateModified: anime.updateDate,
            author: {
              '@type': 'Organization',
              name: '91动漫',
            },
            publisher: {
              '@type': 'Organization',
              name: '91动漫',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20 pb-12 bg-background-100 border-b border-background-200/70">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="mb-5">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: `${categoryMap[anime.category] || anime.category}`, path: `/category/${anime.category}` },
                    { label: anime.title },
                  ]}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px] mx-auto md:mx-0">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={anime.cover}
                      alt={anime.title}
                      title={`${anime.title} - 91动漫高清在线观看`}
                      className="w-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {anime.title}
                  </h1>
                  <p className="text-xs text-foreground-500 mb-4">{anime.titleEn}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {anime.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-medium text-primary-700 bg-primary-100 px-3 py-1 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${
                      anime.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-background-200 text-foreground-600'
                    }`}>
                      {anime.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-background-50 border border-background-200/70">
                      <p className="text-[10px] text-foreground-400 mb-0.5">评分</p>
                      <p className="text-sm font-bold text-foreground-950 flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-sm"></i>
                        {anime.rating}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-50 border border-background-200/70">
                      <p className="text-[10px] text-foreground-400 mb-0.5">总集数</p>
                      <p className="text-sm font-bold text-foreground-950">
                        {anime.episodeCount}集
                        <span className="text-xs text-foreground-400 font-normal ml-1">
                          / 更新至{anime.updatedEpisode}集
                        </span>
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-50 border border-background-200/70">
                      <p className="text-[10px] text-foreground-400 mb-0.5">播放量</p>
                      <p className="text-sm font-bold text-foreground-950">
                        {anime.views > 10000
                          ? `${(anime.views / 10000).toFixed(0)}万`
                          : anime.views}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                    <span>{anime.year}年</span>
                    <span>{anime.season}番</span>
                    <span>制作：{anime.studio}</span>
                    <span>更新于 {anime.updateDate}</span>
                  </div>

                  <Link
                    to={`/anime/${anime.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即观看第{anime.updatedEpisode}集
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <section aria-labelledby="desc-title" className="mb-10">
                  <h2 id="desc-title" className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    番剧简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                    {anime.description}
                  </p>
                </section>

                <section aria-labelledby="episodes-title">
                  <h2 id="episodes-title" className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    剧集列表
                  </h2>
                  <div className="space-y-2">
                    {anime.episodes.map((ep) => (
                      <div
                        key={ep.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                            <i className="ri-play-circle-fill text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-950">
                              第{ep.number}集
                            </p>
                            <p className="text-xs text-foreground-500">{ep.title}</p>
                          </div>
                        </div>
                        <span className="text-xs text-foreground-400">{ep.duration}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <aside className="lg:col-span-1">
                {relatedArticles.length > 0 && (
                  <section className="mb-8" aria-labelledby="related-articles-title">
                    <h3 id="related-articles-title" className="text-base font-heading font-bold text-foreground-950 mb-4">
                      相关资讯
                    </h3>
                    <div className="space-y-3">
                      {relatedArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/anime/${anime.id}`}
                          className="block p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                        >
                          <p className="text-xs font-medium text-foreground-950 line-clamp-2 mb-2">
                            {article.title}
                          </p>
                          <span className="text-[10px] text-foreground-400">{article.date}</span>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {relatedAnime.length > 0 && (
                  <section aria-labelledby="related-anime-title">
                    <h3 id="related-anime-title" className="text-base font-heading font-bold text-foreground-950 mb-4">
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {relatedAnime.map((a) => (
                        <AnimeCard key={a.id} anime={a} showCategory={false} />
                      ))}
                    </div>
                  </section>
                )}
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}